import type { UserPreferences } from '../services/api/userPreferencesService';

export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';

/** 與 NotificationService.getNotificationPriority 對齊：緊急類型可略過勿擾 */
const URGENT_TYPES = new Set<string>(['security_alert']);

export function getNotificationTypeUrgent(type: string): boolean {
  return URGENT_TYPES.has(type);
}

function parseTimeToMinutes(time: string | null | undefined): number | null {
  if (!time || typeof time !== 'string') return null;
  const parts = time.trim().split(':');
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1] ?? '0', 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

/**
 * 判斷目前時間是否落在勿擾區間（支援跨午夜，例如 22:00–08:00）。
 */
export function isWithinDoNotDisturb(
  prefs: Pick<
    UserPreferences,
    'do_not_disturb_enabled' | 'do_not_disturb_start_time' | 'do_not_disturb_end_time'
  > | null,
  at: Date = new Date()
): boolean {
  if (!prefs?.do_not_disturb_enabled) return false;

  const start = parseTimeToMinutes(prefs.do_not_disturb_start_time);
  const end = parseTimeToMinutes(prefs.do_not_disturb_end_time);
  if (start === null || end === null) return false;

  const now = at.getHours() * 60 + at.getMinutes();

  if (start === end) return false;

  if (start < end) {
    return now >= start && now < end;
  }
  // 跨午夜
  return now >= start || now < end;
}

/**
 * 是否應顯示瀏覽器推播（桌面通知）：需開啟 push_notifications，且不在勿擾內（緊急除外）。
 */
export function shouldShowBrowserPushNotification(
  prefs: UserPreferences | null,
  options: { type?: string; priority?: NotificationPriority } = {}
): boolean {
  const pushOn = prefs?.push_notifications !== false;
  if (!pushOn) return false;

  const urgent =
    options.priority === 'urgent' ||
    (options.type ? getNotificationTypeUrgent(options.type) : false);
  if (urgent) return true;

  if (isWithinDoNotDisturb(prefs)) return false;
  return true;
}

/**
 * 是否允許使用已儲存的偏好位置（與「位置追蹤」開關一致）。
 */
export function isLocationTrackingEnabled(prefs: UserPreferences | null): boolean {
  return prefs?.location_tracking !== false;
}

/**
 * 前端／同進程快速判斷。正式寄送「可選」郵件／簡訊請優先呼叫 DB：
 * `public.user_pref_allows_notification_channel(user_id, 'email'|'sms', bypass_prefs)`
 * （勿擾以 Asia/Taipei 計算，與下列邏輯一致；必要通知傳 bypass_prefs = true）。
 */
export function shouldSendEmailByPreference(prefs: UserPreferences | null): boolean {
  return prefs?.email_notifications !== false;
}

export function shouldSendSmsByPreference(prefs: UserPreferences | null): boolean {
  return prefs?.sms_notifications !== false;
}

/**
 * 綜合判斷是否應發送「可延後」的推播類通知（與 shouldShowBrowserPushNotification 一致邏輯，供後端複用概念）。
 */
export function shouldDeliverPushStyleNotification(
  prefs: UserPreferences | null,
  options: { type?: string; priority?: NotificationPriority } = {}
): boolean {
  return shouldShowBrowserPushNotification(prefs, options);
}

/** 點擊推播／前景橫幅時導向的路徑 */
export function getNotificationOpenPath(type: string): string {
  if (type.startsWith('job_')) return '/craftsman/jobs';
  if (type.startsWith('message_')) return '/messages';
  return '/notifications';
}
